import { PRODUCT_NAMES } from '../../../../constants/product-names';
import { EvercycleExclusionReason } from '../../../../data/models/recommendations.models';

/**
 * Static copy tokens for the EverCycle Buyback surface. Kept in one file so
 * the plugin, badge and (future) renewal-modal choice stay in sync and so the
 * exclusion-reason map is a single source of truth.
 *
 * Program label and reason copy come from spec 002/003's "Copy tokens" section.
 * The program label itself is sourced from `PRODUCT_NAMES.EVERCYCLE_BUYBACK`
 * (added to the single source of truth in `core/src/constants/product-names.ts`),
 * so future brand changes propagate through one edit.
 */
export const EVERCYCLE_PROGRAM_LABEL = PRODUCT_NAMES.EVERCYCLE_BUYBACK;

export const EVERCYCLE_BADGE_LABEL = 'Buyback eligible';

export const EVERCYCLE_PENDING_LABEL = 'Valuation pending';

/**
 * Program-level documentation page. Not environment-specific, so a constant rather than runtime
 * config; it must stay the same page the renewal emails link to.
 */
export const EVERCYCLE_DOCUMENTATION_URL = 'https://www.purestorage.com/products/evergreen/evercycle.html';

/** Caption above the valuation amount on the card and in the dialog. */
export const EVERCYCLE_VALUATION_CAPTION = 'Estimated Trade-In Value';

/** Qualifier under the valuation amount: the final payment depends on the inspection. */
export const EVERCYCLE_VALUATION_QUALIFIER = 'Subject to inspection';

/** Terms shown above the acceptance checkbox in the dialog. */
export const EVERCYCLE_PAYMENT_TERMS =
    'Final payment is subject to receipt of all listed components with matching serial numbers and successful ' +
    "inspection. After you accept the valuation, you'll receive a return shipping label. " +
    `${PRODUCT_NAMES.COMPANY_NAME} will issue a purchase order to your organization, and the hardware must be ` +
    `shipped to the designated ${PRODUCT_NAMES.COMPANY_NAME} warehouse.`;

export const EVERCYCLE_EXCLUSION_COPY: Readonly<Record<EvercycleExclusionReason, string>> = Object.freeze({
    XR2_CONTROLLER: 'XR2 controllers are not eligible for buyback',
    SAS_DATAPACK: 'Upgraded SAS drives are not eligible for buyback',
    PRE_FB_S_GENERATION: 'FlashBlade generations before FlashBlade//S are not eligible for buyback',
});

/**
 * Resolve the human-readable copy for an exclusion reason. Throws on an
 * unknown reason rather than silently defaulting — this fails tests fast when
 * a new reason code is added upstream and the UI is not updated.
 */
export function exclusionReasonCopy(reason: EvercycleExclusionReason): string {
    const copy = EVERCYCLE_EXCLUSION_COPY[reason];
    if (!copy) {
        throw new Error(`EverCycle: no copy registered for exclusion reason "${reason}"`);
    }
    return copy;
}
